import { withTimeSystem } from "../../constants/orbitMetadata";
import type { FormatterMap } from "../../helpers/utils";
import {
  getConjunctionMetricTone,
  getProbabilityFamilyTone,
  getTimeToTcaSeconds,
  type ConjunctionRiskConfig,
} from "../conjunctions/conjunctionUiUtils";
import { EventEvolutionField } from "./EventEvolutionField";
import type { EventEvolutionEvent } from "./EventEvolutionTypes";
import {
  MISS_DISTANCE_METRIC,
  PROBABILITY_METRICS,
  SEPARATION_METRICS,
  formatDate,
  formatMetricValueWithConjunctionFormatters,
  getMetricTooltip,
  type EventMetricDefinition,
} from "./eventEvolutionUtils";

interface EventRecordDetailsProps {
  event?: EventEvolutionEvent;
  formatters?: FormatterMap;
  riskConfig?: ConjunctionRiskConfig;
}

interface MetricFieldOptions {
  label?: string;
  strong?: boolean;
  tooltip?: string;
  value?: string;
}

export const EventRecordDetails = ({
  event,
  formatters,
  riskConfig,
}: EventRecordDetailsProps) => {
  const timeToTcaTone = getConjunctionMetricTone(
    getTimeToTcaSeconds(new Date().toISOString(), event?.tca),
    riskConfig,
    "timeTCA",
  );

  const renderMetricField = (
    metric: EventMetricDefinition,
    { label, strong, tooltip, value }: MetricFieldOptions = {},
  ) => (
    <EventEvolutionField
      key={`${metric.id}-${label ?? metric.label}`}
      label={label ?? metric.label}
      value={formatMetricValueWithConjunctionFormatters(
        event,
        metric,
        formatters,
        value,
      )}
      tooltip={getMetricTooltip(event, metric, tooltip)}
      strong={strong}
      tone={
        metric.id === "minDistance"
          ? getConjunctionMetricTone(
              value ?? event?.minDistance,
              riskConfig,
              "minDist",
            )
          : metric.id === "radial"
            ? getConjunctionMetricTone(
                value ?? event?.radial,
                riskConfig,
                "radDist",
              )
            : metric.id === "along"
              ? getConjunctionMetricTone(
                  value ?? event?.along,
                  riskConfig,
                  "alongDist",
                )
              : metric.id === "cross"
                ? getConjunctionMetricTone(
                    value ?? event?.cross,
                    riskConfig,
                    "crossDist",
                  )
                : getProbabilityFamilyTone(value ?? event?.poc, riskConfig)
      }
    />
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
        <EventEvolutionField
          label={withTimeSystem("TCA")}
          value={formatDate(event?.tca)}
          tooltip="Time of Closest Approach"
          strong
          tone={timeToTcaTone}
        />
        {renderMetricField(MISS_DISTANCE_METRIC, { strong: true })}
      </div>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
          Separation
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
          {SEPARATION_METRICS.map((metric) => renderMetricField(metric))}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
          Probability
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
          {PROBABILITY_METRICS.map((metric) => renderMetricField(metric))}
        </div>
      </section>
    </div>
  );
};
